import { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  children?: React.ReactNode;
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType,
  ogUrl,
  twitterCard,
  jsonLd,
  children,
}: PageMetaProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (ogTitle) setMeta('og:title', ogTitle, true);
    if (ogDescription) setMeta('og:description', ogDescription, true);
    if (ogType) setMeta('og:type', ogType, true);
    if (ogUrl) setMeta('og:url', ogUrl, true);

    if (twitterCard) setMeta('twitter:card', twitterCard);
    if (ogTitle) setMeta('twitter:title', ogTitle);
    if (ogDescription) setMeta('twitter:description', ogDescription);

    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) existingCanonical.remove();
    if (canonicalUrl) {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    const existingJsonLdScripts = document.querySelectorAll('script[type="application/ld+json"][data-page-meta]');
    existingJsonLdScripts.forEach((s) => s.remove());
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-page-meta', 'true');
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"][data-page-meta]');
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, ogUrl, twitterCard, jsonLd]);

  return <>{children}</>;
}